import React, {createContext, useCallback, useContext, useEffect, useState} from "react";
import moment from "moment";
import {ConversationRepository} from "../repositories/ConversationRepository";
import {useStompWebsocket} from "../hooks/useStompWebsocket";

export const DashboardContext = createContext({
    responseTime: [],
    statuses: {},
    conversations: [],
    pagination: {page: 0, size: 25, totalElements: 0},
    nodeUsage: {},
    wsState: "inactive",
    updatedValue: null,
    tagUsage: {},
    dateRange: null,
    setDateRange: () => {
    },
    refreshInterval: null,
    setRefreshInterval: () => {
    },
    lastFetched: null,
    refreshResponseTime: () => {
    },
    refreshStatuses: () => {
    },
    refreshConversations: () => {
    },
    refreshNodeUsage: () => {
    },
    refreshTagsUsage: () => {
    },
    refreshAll: () => {
    },
    setPagination: () => {
    },
});
const resolveRange = (dateRange) => {
    if (dateRange?.start && dateRange?.end) {
        return {start: dateRange.start, end: dateRange.end};
    }
    const end = moment();
    return {start: end.clone().subtract(24, "hours"), end};
};

export const DashboardContextProvider = ({project, children}) => {
    const [updatedValue, wsState] = useStompWebsocket(project?.id ? `/topic/flow/${project?.id}` : null);

    const [responseTime, setResponseTime] = useState([]);
    const [statuses, setStatuses] = useState({});
    const [conversations, setConversations] = useState([]);
    const [pagination, setPagination] = useState({page: 0, size: 25, totalElements: 0});
    const [nodeUsage, setNodeUsage] = useState({});
    const [tagUsage, setTagUsage] = useState({});
    const [tagsUsageLoading, setTagsUsageLoading] = useState(true);
    const [responseTimeRange, setResponseTimeRange] = useState(() => resolveRange(null));
    // dateRange: {label, start, end} (moment instances) | null — applies to stats only, NOT to the conversations list
    const [dateRange, setDateRange] = useState(null);
    // refreshInterval: milliseconds | 60000 — how often stats (NOT conversations) auto-refetch
    const [refreshInterval, setRefreshInterval] = useState(60000);
    // lastFetched: Date | null — when the stats (NOT conversations) were last successfully refreshed
    const [lastFetched, setLastFetched] = useState(null);

    // Fetch response time data
    const refreshResponseTime = useCallback(async (signal) => {
        if (!project?.id) return;

        const range = resolveRange(dateRange);
        try {
            const response = await ConversationRepository.getResponseTime({
                flowUid: project.id,
                start: range.start.format("YYYY-MM-DDTHH:mm:ss"),
                end: range.end.format("YYYY-MM-DDTHH:mm:ss"),
                signal
            });
            setResponseTime(response?.data || []);
            setResponseTimeRange(range);
        } catch (error) {
            if (error.name !== "CanceledError" && error.name !== "AbortError") {
                console.error("Error fetching response time:", error);
            }
        }
    }, [project?.id, dateRange]);

    // Fetch status statistics
    const refreshStatuses = useCallback(async (signal) => {
        if (!project?.id) return;

        const range = resolveRange(dateRange);
        try {
            const response = await ConversationRepository.getStatusStatistic({
                flowUid: project.id,
                type: project?.settings?.projectType || "VOICE_BOT",
                start: range.start.format("YYYY-MM-DDTHH:mm:ss"),
                end: range.end.format("YYYY-MM-DDTHH:mm:ss"),
                signal
            });
            setStatuses(response?.data || {});
        } catch (error) {
            if (error.name !== "CanceledError" && error.name !== "AbortError") {
                console.error("Error fetching statuses:", error);
            }
        }
    }, [project?.id, dateRange]);


    // Fetch conversations — intentionally NOT affected by dateRange
    const refreshConversations = useCallback(async (filters = {}) => {
        if (!project?.id) return;

        try {
            const response = await ConversationRepository.getConversations({
                flow: project.id,
                page: pagination?.page || 0,
                size: pagination?.size || 25,
                type: project?.settings?.projectType || "VOICE_BOT",
                ...filters,
            });

            setPagination(prev => ({
                ...prev,
                totalElements: response?.data?.totalElements || 0
            }));
            setConversations(response?.data?.content || []);
        } catch (error) {
            console.error("Error fetching conversations:", error);
        }
    }, [project?.id, pagination?.page, pagination?.size]);

    // Fetch node usage data
    const refreshNodeUsage = useCallback(async () => {
        if (!project?.id) return;

        const tags = project?.settings?.tags || [];
        const ids = tags?.map(tag => tag?.nodes || [])?.flat(1);

        if (ids.length === 0) {
            setNodeUsage({});
            return;
        }
        const range = resolveRange(dateRange);
        try {
            const response = await ConversationRepository.getNodeUsage(ids, {
                start: range.start.format("YYYY-MM-DDTHH:mm:ss"),
                end: range.end.format("YYYY-MM-DDTHH:mm:ss"),
            });
            setNodeUsage(response?.data || {});
        } catch (error) {
            console.error("Error fetching node usage:", error);
        }
    }, [project?.id, project?.settings?.tags, dateRange]);

    const refreshTagsUsage = useCallback(async (start, end) => {
        if (!project?.id) return;

        const tags = project?.settings?.tags || [];
        const map = {}
        for (const tag of tags) {
            map[tag.name] = tag.id;
        }

        if (Object.keys(map).length === 0) {
            setTagUsage({});
            setTagsUsageLoading(false);
            return;
        }

        // explicit start/end (e.g. a component's own picker) override the global dateRange
        const base = resolveRange(dateRange);
        const rangeStart = start !== undefined ? start : base.start;
        const rangeEnd = end !== undefined ? end : base.end;

        setTagsUsageLoading(true);

        try {
            const response = await ConversationRepository.getTagsUsage({
                project_id: project?.id,
                tags: map,
                type: project?.settings?.projectType || "VOICE_BOT",
                start: rangeStart ? rangeStart.format("YYYY-MM-DDTHH:mm:ss") : undefined,
                end: rangeEnd ? rangeEnd.format("YYYY-MM-DDTHH:mm:ss") : undefined
            });
            setTagUsage(response?.data || {});
        } catch (error) {
            console.error("Error fetching tags usage:", error);
        } finally {
            setTagsUsageLoading(false);
        }
    }, [project?.id, project?.settings?.tags, dateRange]);

    const refreshDashboardStats = useCallback(async (signal) => {
        await Promise.all([
            refreshResponseTime(signal),
            refreshStatuses(signal),
            refreshNodeUsage(),
            refreshTagsUsage()
        ]);
    }, [
        refreshResponseTime,
        refreshStatuses,
        refreshNodeUsage,
        refreshTagsUsage
    ]);


    useEffect(() => {
        if (!project?.id || !updatedValue) return;

        const controller = new AbortController();

        const timeoutId = setTimeout(() => {
            refreshStatuses(controller.signal)
                .then(() => setLastFetched(new Date()));
        }, 3000);

        return () => {
            clearTimeout(timeoutId);
            controller.abort();
        };
    }, [
        project?.id,
        updatedValue,
        refreshStatuses
    ]);

    // Refresh all data

    const refreshAll = useCallback(async () => {
        const controller = new AbortController();

        await Promise.all([
            refreshDashboardStats(controller.signal),
            refreshConversations()
        ]);

        return () => controller.abort();
    }, [
        refreshDashboardStats,
        refreshConversations
    ]);

    // Re-fetch stats (NOT conversations) whenever the date range changes
    useEffect(() => {
        if (!project?.id) return;

        const controller = new AbortController();

        refreshDashboardStats(controller.signal)
            .then(() => setLastFetched(new Date()));

        return () => controller.abort();
    }, [dateRange, project?.id, refreshDashboardStats]);

    // Periodically re-fetch stats (NOT conversations) at the chosen interval
    useEffect(() => {
        if (!project?.id || !refreshInterval) return;

        const intervalId = setInterval(() => {
            const controller = new AbortController();
            refreshDashboardStats(controller.signal)
                .then(() => setLastFetched(new Date()));
        }, refreshInterval);

        return () => clearInterval(intervalId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [project?.id, refreshInterval, dateRange, refreshDashboardStats]);

    const value = {
        responseTime,
        statuses,
        conversations,
        pagination,
        nodeUsage,
        wsState,
        tagUsage,
        tagsUsageLoading,
        updatedValue,
        dateRange,
        setDateRange,
        refreshInterval,
        setRefreshInterval,
        lastFetched,
        refreshResponseTime,
        refreshStatuses,
        refreshConversations,
        refreshNodeUsage,
        refreshTagsUsage,
        refreshAll,
        setPagination,
        refreshDashboardStats,
        responseTimeRange
    };

    return (
        <DashboardContext.Provider value={value}>
            {children}
        </DashboardContext.Provider>
    );
};

export const useDashboard = () => {
    const context = useContext(DashboardContext);
    if (!context) {
        throw new Error("useDashboard must be used within DashboardContextProvider");
    }
    return context;
};
